import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Book, Plus, LogOut, User, Download, Eye, Clock, FileText, Printer, Package, ChevronRight, Headphones } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [autobiographies, setAutobiographies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bundleOrders, setBundleOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingId, setViewingId] = useState(null);

  useEffect(() => {
    fetchAutobiographies();
    fetchOrders();
    fetchBundleOrders();
  }, [user]);

  const fetchAutobiographies = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/autobiographies/mine?email=${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const data = await res.json();
        setAutobiographies(data);
      }
    } catch (err) {
      console.error('Failed to fetch autobiographies:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!user?.email) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/hardcover/orders?email=${encodeURIComponent(user.email)}`);
      if (res.ok) setOrders(await res.json());
    } catch (err) { console.error('Failed to fetch orders:', err); }
  };

  const fetchBundleOrders = async () => {
    if (!user?.email) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/bundle/orders?email=${encodeURIComponent(user.email)}`);
      if (res.ok) setBundleOrders(await res.json());
    } catch (err) { console.error('Failed to fetch bundle orders:', err); }
  };

  const handleDownloadAudio = async (autobiographyId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/audio/download/${autobiographyId}?email=${encodeURIComponent(user.email)}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audiobook.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Download Started', description: 'Your audiobook is downloading.' });
    } catch {
      toast({ title: 'Download Failed', description: 'Audiobook not available yet.', variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      document.cookie = 'session_token=; path=/; max-age=0';
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  const handleDownloadPDF = async (auto) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/autobiography/${auto.id}/download/pdf`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${auto.title.replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Download Started', description: 'Your PDF is downloading.' });
    } catch {
      toast({ title: 'Download Failed', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleView = (auto) => {
    navigate(`/autobiography/${auto.id}`, { state: { autobiographyData: auto } });
  };

  const handleCreateNew = () => {
    navigate('/', { state: { startQuestionnaire: false } });
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Book className="w-8 h-8 text-purple-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              Life Echo
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <User className="w-8 h-8 text-gray-400" />
              )}
              <span className="text-gray-700 font-medium hidden md:block" data-testid="user-name">{user?.name}</span>
            </div>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12" data-testid="dashboard-content">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-10 gap-4">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-1" data-testid="dashboard-welcome">
              Welcome back, {user?.name?.split(' ')[0] || 'there'}
            </h2>
            <p className="text-gray-600 text-lg">Your life stories, preserved forever</p>
          </div>
          <Button
            onClick={handleCreateNew}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-6 py-5 text-base rounded-full flex items-center gap-2 border-0 shadow-lg"
            data-testid="create-new-btn"
          >
            <Plus className="w-5 h-5" />
            Create New Story
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : autobiographies.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 shadow-lg text-center" data-testid="empty-state">
            <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">No autobiographies yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Start your first autobiography by answering a few simple questions about your life.
            </p>
            <Button
              onClick={handleCreateNew}
              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full px-8 py-5 border-0"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
          </div>
        ) : (
          <div className="grid gap-6" data-testid="autobiographies-list">
            {autobiographies.map((auto) => (
              <div
                key={auto.id}
                className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow p-6 flex flex-col md:flex-row md:items-center gap-4"
                data-testid={`autobiography-card-${auto.id}`}
              >
                {/* Book icon */}
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-7 h-7 text-purple-500" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-800 truncate" data-testid="autobiography-title">
                    {auto.title}
                  </h3>
                  <p className="text-sm text-gray-500 italic mb-1">{auto.subtitle}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(auto.created_at)}
                    </span>
                    <span>{auto.word_count?.toLocaleString()} words</span>
                    <span>{auto.chapters} chapters</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(auto)}
                    className="flex items-center gap-1 rounded-lg"
                    data-testid={`view-btn-${auto.id}`}
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(auto)}
                    className="flex items-center gap-1 rounded-lg"
                    data-testid={`download-btn-${auto.id}`}
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/hardcover-order/${auto.id}`)}
                    className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white border-0"
                    data-testid={`order-hardcover-btn-${auto.id}`}
                  >
                    <Printer className="w-4 h-4" />
                    Hardcover
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/bundle-order/${auto.id}`)}
                    className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0"
                    data-testid={`order-bundle-btn-${auto.id}`}
                  >
                    <Package className="w-4 h-4" />
                    Bundle
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hardcover Orders Section */}
        {orders.length > 0 && (
          <div className="mt-12" data-testid="orders-section">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Package className="w-6 h-6 text-purple-500" />
              Book Orders
            </h3>
            <div className="grid gap-4">
              {orders.map((order) => {
                const statusColors = {
                  pending_payment: 'bg-yellow-100 text-yellow-700',
                  submitted_to_printer: 'bg-blue-100 text-blue-700',
                  lulu_failed: 'bg-orange-100 text-orange-700',
                  CREATED: 'bg-blue-100 text-blue-700',
                  UNPAID: 'bg-yellow-100 text-yellow-700',
                  PRODUCTION_READY: 'bg-indigo-100 text-indigo-700',
                  IN_PRODUCTION: 'bg-purple-100 text-purple-700',
                  SHIPPED: 'bg-green-100 text-green-700',
                  DELIVERED: 'bg-green-100 text-green-700',
                };
                const displayStatus = order.lulu_status || order.status;
                const statusClass = statusColors[displayStatus] || 'bg-gray-100 text-gray-700';

                return (
                  <div key={order.order_id} className="bg-white rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`order-${order.order_id}`}>
                    <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm">Order #{order.order_id?.substring(0, 8)}</p>
                      <p className="text-xs text-gray-500">{order.quantity}x Hardcover &middot; {formatDate(order.created_at)}</p>
                      <p className="text-xs text-gray-500">{order.shipping_name} &middot; {order.city}, {order.country_code}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass}`}>{displayStatus?.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-bold text-gray-800">£{(order.amount / 100).toFixed(0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bundle Orders Section */}
        {bundleOrders.length > 0 && (
          <div className="mt-12" data-testid="bundle-orders-section">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Headphones className="w-6 h-6 text-amber-500" />
              Bundle Orders
            </h3>
            <div className="grid gap-4">
              {bundleOrders.map((order) => {
                const audioStatusColors = {
                  pending: 'bg-gray-100 text-gray-600',
                  pending_voice: 'bg-yellow-100 text-yellow-700',
                  generating: 'bg-blue-100 text-blue-700',
                  completed: 'bg-green-100 text-green-700',
                  failed: 'bg-red-100 text-red-700',
                };
                const audioClass = audioStatusColors[order.audio_status] || 'bg-gray-100 text-gray-600';

                return (
                  <div key={order.order_id} className="bg-white rounded-xl shadow-sm p-5" data-testid={`bundle-order-${order.order_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm">Bundle #{order.order_id?.substring(0, 8)}</p>
                        <p className="text-xs text-gray-500">Book + Audiobook + PDF &middot; {formatDate(order.created_at)}</p>
                        <p className="text-xs text-gray-500">{order.shipping_name} &middot; {order.city}, {order.country_code}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${audioClass}`}>
                          Audio: {order.audio_status?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-bold text-gray-800">£{(order.amount / 100).toFixed(2)}</span>
                      </div>
                    </div>
                    {order.audio_status === 'completed' && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <Button
                          size="sm"
                          onClick={() => handleDownloadAudio(order.autobiography_id)}
                          className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg border-0"
                          data-testid={`download-audio-${order.order_id}`}
                        >
                          <Headphones className="w-4 h-4" />
                          Download Audiobook (MP3)
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
